import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Asset } from '../api/types';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

export interface PendingAttachment {
  id: string; // local id (random) hasta que upload completa
  name: string;
  mimeType: string;
  sizeBytes: number;
  previewUrl: string; // ObjectURL local para mostrar inmediato
  status: 'uploading' | 'done' | 'error';
  error?: string;
  asset?: Asset; // poblado al completar
}

export function useChatUpload(chatId: string | undefined) {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const qc = useQueryClient();

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!chatId || files.length === 0) return;

      // Resize de imágenes pesadas antes de subir. Las fotos de cámara móvil
      // (4000×3000, 6-12 MB) hacen abortar la subida en redes celulares —
      // bajamos a max 2048 px / JPEG q0.85 para que pesen ~500 kB-1.5 MB.
      const prepared = await Promise.all(files.map(maybeResize));

      // Crear placeholders inmediatos con previewUrl local
      const placeholders: PendingAttachment[] = prepared.map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: f.name || `pegado-${new Date().toISOString().slice(11, 19)}.${guessExt(f.type)}`,
        mimeType: f.type || 'application/octet-stream',
        sizeBytes: f.size,
        previewUrl: URL.createObjectURL(f),
        status: 'uploading',
      }));

      setAttachments((prev) => [...prev, ...placeholders]);

      // Upload en paralelo
      await Promise.all(
        placeholders.map(async (placeholder, idx) => {
          const file = prepared[idx];
          try {
            const fd = new FormData();
            fd.append('file', file, placeholder.name);
            const res = await fetch(`${BASE}/chats/${chatId}/assets`, {
              method: 'POST',
              credentials: 'include',
              body: fd,
            });
            if (!res.ok) {
              const errText = await res.text().catch(() => `HTTP ${res.status}`);
              throw new Error(errText);
            }
            const asset = (await res.json()) as Asset;
            setAttachments((prev) =>
              prev.map((a) =>
                a.id === placeholder.id ? { ...a, status: 'done', asset } : a,
              ),
            );
            // Refrescar el ReferenceTray para que la imagen aparezca apenas sube
            qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] });
          } catch (err) {
            setAttachments((prev) =>
              prev.map((a) =>
                a.id === placeholder.id
                  ? { ...a, status: 'error', error: (err as Error).message }
                  : a,
              ),
            );
          }
        }),
      );
    },
    [chatId, qc],
  );

  const remove = useCallback((id: string) => {
    setAttachments((prev) => {
      const removed = prev.find((a) => a.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((a) => URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  }, []);

  /** IDs de assets ya subidos exitosamente (listos para mandar como `attachments`) */
  const completedAssetIds = attachments
    .filter((a) => a.status === 'done' && a.asset)
    .map((a) => a.asset!.id);

  const allDone = attachments.every((a) => a.status === 'done');

  return { attachments, addFiles, remove, clear, completedAssetIds, allDone };
}

function guessExt(mime: string): string {
  const map: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
  };
  return map[mime] ?? 'bin';
}

/**
 * Reduce imágenes pesadas a max 2048 px / JPEG q0.85 antes de subirlas.
 * Las fotos de cámara móvil son 4000×3000 / 6-12 MB → la subida tarda 10-30 s
 * en celular y la conexión aborta a mitad. Resize trae eso a ~500 kB-1.5 MB.
 *
 * Skips: GIF (perdería animación), SVG (vector), no-imágenes (video/audio/pdf),
 * o imágenes ya chicas (<1.5 MB). Si el decode falla (HEIC sin soporte del
 * navegador) devolvemos el original sin romper.
 */
async function maybeResize(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;
  if (file.size < 1_500_000) return file;

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const MAX = 2048;
    let { width, height } = img;
    if (width <= MAX && height <= MAX && file.size < 3_000_000) return file;
    if (width > MAX || height > MAX) {
      const scale = Math.min(MAX / width, MAX / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    const newName = file.name.replace(/\.(png|webp|heic|heif|jpg|jpeg)$/i, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}
